import type { ReactNode } from 'react';
import { Mail, Phone, MessageCircle, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CopyButton } from '@/components/CopyButton';
import type { InlineContact } from '@/lib/contactTypes';

/**
 * The one contact card body. Rendered inside a popover on desktop and a bottom
 * sheet on mobile, so a person reads identically everywhere in the app.
 *
 * Layout is fixed: identity header, contact rows, one primary action plus
 * icon-only secondary channels on a single row, quiet context line last.
 */
export interface ContactChannel {
  key: 'email' | 'phone' | 'sms' | 'whatsapp';
  label: string;
  href: string;
  external?: boolean;
}

const channelIcon: Record<ContactChannel['key'], typeof Mail> = {
  email: Mail,
  phone: Phone,
  sms: MessageSquare,
  whatsapp: MessageCircle,
};

export const initialsOf = (name: string): string =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

interface Props {
  contact: InlineContact;
  /** Quiet last line: what this person did, or where the contact came from. */
  contextLine?: string | null;
  channels: ContactChannel[];
  /** Omitted when there is no in-app target (no company). */
  onMessage?: () => void;
  onChannelClick?: (key: ContactChannel['key']) => void;
  /** Larger touch targets for the mobile sheet. */
  dense?: boolean;
  footer?: ReactNode;
}

export default function ContactCardBody({
  contact,
  contextLine,
  channels,
  onMessage,
  onChannelClick,
  dense = true,
  footer,
}: Props) {
  const rowText = dense ? 'text-xs' : 'text-sm';
  const iconBtn = dense ? 'h-8 w-8' : 'h-10 w-10';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        <Avatar className={dense ? 'h-9 w-9' : 'h-11 w-11'}>
          <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
            {initialsOf(contact.full_name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-tight">{contact.full_name}</div>
          {contact.company_name && (
            <div className="truncate text-xs text-muted-foreground">{contact.company_name}</div>
          )}
        </div>
      </div>

      {(contact.email || contact.phone) && (
        <div className="space-y-1 border-t border-border pt-2.5">
          {contact.email && (
            <div className="group flex items-center gap-1">
              <span className={`min-w-0 flex-1 select-text break-all text-muted-foreground ${rowText}`}>
                {contact.email}
              </span>
              <CopyButton
                value={contact.email}
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
              />
            </div>
          )}
          {contact.phone && (
            <div className="group flex items-center gap-1">
              <span className={`min-w-0 flex-1 select-text text-muted-foreground ${rowText}`}>
                {contact.phone}
              </span>
              <CopyButton
                value={contact.phone}
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
              />
            </div>
          )}
        </div>
      )}

      {!contact.email && !contact.phone && (
        <p className="border-t border-border pt-2.5 text-xs text-muted-foreground">
          No email or phone on file — message them here instead.
        </p>
      )}

      <div className="flex items-center gap-1.5 border-t border-border pt-2.5">
        {onMessage && (
          <Button size="sm" className={`gap-1.5 ${dense ? 'h-8' : 'h-10'} flex-1 sm:flex-none`} onClick={onMessage}>
            <MessageSquare className="h-3.5 w-3.5" />
            Message
          </Button>
        )}
        {channels.length > 0 && (
          <TooltipProvider delayDuration={200}>
            <div className={`flex items-center gap-1 ${onMessage ? 'ml-auto' : ''}`}>
              {channels.map((ch) => {
                const Icon = channelIcon[ch.key];
                return (
                  <Tooltip key={ch.key}>
                    <TooltipTrigger asChild>
                      <Button
                        asChild
                        size="icon"
                        variant="outline"
                        className={iconBtn}
                        onClick={() => onChannelClick?.(ch.key)}
                      >
                        <a
                          href={ch.href}
                          aria-label={ch.label}
                          {...(ch.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          <Icon className="h-4 w-4" />
                        </a>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">{ch.label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </div>

      {contextLine && (
        <div className="border-t border-border pt-2 text-xs text-muted-foreground">{contextLine}</div>
      )}
      {footer}
    </div>
  );
}
